import classNames from "classnames";
import { FC, PropsWithChildren, ReactNode, useRef, useState } from "react";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { Glyph } from "../../types";
import SvgAdd from "../icons/Add";
import SvgArrowDropDown from "../icons/ArrowDropDown";
import SvgEdit from "../icons/Edit";
import SvgQuestionMark from "../icons/SvgQuestionMark";
import styles from "./Box.module.css";
import Button from "./Button";
import Icon from "./Icon";
import Inline from "./Inline";

export type BoxField = {
  title?: string;
  data: ReactNode;
  colspan?: number;
  addElement?: ReactNode;
  showHelp?: {
    type?: "info" | "warning" | "error";
    onOpen?: () => void;
  };
};

export type BoxFieldButton = {
  title?: string;
  onClick: () => void;
  type?: string;
};

export type ListAction = {
  label: string;
  onClick: () => void;
  icon?: Glyph;
  disabled?: boolean;
};

type BoxProps = PropsWithChildren<{
  title?: string;
  onEdit?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  button?: BoxFieldButton;
  fields?: [BoxField | null, BoxField | null][];
  listActions?: ListAction[];
}>;

const Box = ({ title, onEdit, button, children, fields, listActions }: BoxProps) => {
  const renderTdHeader = (field: BoxField) =>
    !!field.title && (
      <div className={styles.title}>
        <strong>{field.title}</strong>
        {!!field.showHelp && (
          <Icon
            glyph={SvgQuestionMark}
            className={styles.help}
            onClick={field.showHelp.onOpen}
            fill={field.showHelp.type === "error" ? "var(--red)" : undefined}
          />
        )}
      </div>
    );

  const renderTdBody = (field: BoxField) => {
    const data = <div className={styles.data}>{field?.data}</div>;
    return field.addElement ? (
      <Inline>
        {data}
        {field.addElement}
      </Inline>
    ) : (
      data
    );
  };

  const boxMenuRef = useRef<HTMLDivElement>(null);
  const [showListActionsMenu, setShowListActionsMenu] = useState(false);

  useOnClickOutside(boxMenuRef, () => setShowListActionsMenu(false));

  return (
    <div className={styles.box}>
      <div className={styles.header}>
        {title ? <h3 className={styles.title}>{title}</h3> : undefined}
        {!!onEdit && (
          <Button
            buttonProps={{
              onClick: onEdit,
            }}
            type="secondary"
            glyph={SvgEdit}
          />
        )}
        {!!listActions?.length && (
          <div
            ref={boxMenuRef}
            className={classNames(styles["menu-container"], {
              [styles.open]: showListActionsMenu,
            })}
          >
            <Button
              buttonProps={{ onClick: () => setShowListActionsMenu(!showListActionsMenu) }}
              glyph={SvgArrowDropDown}
            />
            <div className={styles.menu}>
              {listActions.map((action, index) => (
                <div
                  key={`action_${index}`}
                  onClick={(e) => {
                    if (!action.disabled) {
                      e.preventDefault();
                      action.onClick();
                      setShowListActionsMenu(false);
                    }
                  }}
                  className={classNames(styles.action, action.disabled ? styles.disabled : undefined)}
                >
                  {action.icon && <Icon glyph={action.icon} className={styles.icon} />}
                  {action.label}
                </div>
              ))}
            </div>
          </div>
        )}

        {button && (
          <Button
            buttonProps={{
              onClick: button?.onClick,
            }}
            glyph={!button.title ? SvgAdd : undefined}
          >
            {button.title ?? undefined}
          </Button>
        )}
      </div>
      {!!fields?.length && (
        <div className={styles.fields}>
          {fields.map(([left, right], idx) => (
            <div key={`_field_${idx}`} className={styles.row}>
              {!!left && (
                <div className={classNames(styles.field, !!left.colspan && styles["field-full"])}>
                  {renderTdHeader(left)}
                  {renderTdBody(left)}
                </div>
              )}
              {!!right && (
                <div className={styles.field}>
                  {renderTdHeader(right)}
                  {renderTdBody(right)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {children}
    </div>
  );
};

interface BoxIconProps {
  glyph: Glyph;
  onClick: () => void;
}

const BoxIcon: FC<BoxIconProps> = ({ glyph, onClick }) => (
  <div className={classNames(styles.icon, styles.data)} onClick={onClick}>
    <Icon glyph={glyph} />
  </div>
);

Box.Icon = BoxIcon;

type BoxGroupProps = {
  boxs: ReactNode | ReactNode[];
  color?: "yellow" | "red";
};

const BoxGroup: FC<BoxGroupProps> = ({ boxs, color }: BoxGroupProps) => (
  <div
    className={classNames(styles.group, {
      [styles.red]: color === "red",
      [styles.yellow]: color === "yellow",
    })}
  >
    {(Array.isArray(boxs) ? boxs : [boxs]).map(
      (box, idx) =>
        !!box && (
          <div className={classNames(styles.element, idx === 0 ? styles["first-element"] : undefined)} key={idx}>
            {box}
          </div>
        ),
    )}
  </div>
);

Box.Group = BoxGroup;

export default Box;
